import { useEffect, useRef, useState } from "react";
import { MessageCircle, RefreshCw, Search, Send } from "lucide-react";
import axiosInstance from "../../api/axiosInstance";
import "./SupportInbox.css";

const STATUS_LABELS = {
  bot: "AI assistant",
  waiting_support: "Waiting for support",
  with_support: "With support",
  closed: "Closed",
};

// Derive the WebSocket base from the REST API URL (http -> ws, https -> wss)
const WS_BASE = (process.env.REACT_APP_API_URL || "").replace(/^http/, "ws");

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function getPreview(session) {
  return session.latest_message_content || "No messages yet";
}

function sortSessions(items) {
  return [...items].sort((left, right) => new Date(right.updated_at) - new Date(left.updated_at));
}

export default function SupportInbox() {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [query, setQuery] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  async function loadSessions(showLoading = true) {
    if (showLoading) setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get("/api/support/chat/support/sessions/");
      const nextSessions = sortSessions(Array.isArray(data) ? data : []);
      setSessions(nextSessions);
      setSelectedSessionId((current) => current || nextSessions[0]?.session_id || null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Unable to load support conversations.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }

  useEffect(() => {
    loadSessions();
    const refreshInterval = window.setInterval(() => loadSessions(false), 30000);
    return () => window.clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (!selectedSessionId) {
      setSelectedSession(null);
      return;
    }

    let cancelled = false;
    setSelectedSession(null);
    axiosInstance.get(`/api/support/chat/support/sessions/${selectedSessionId}/`)
      .then(({ data }) => {
        if (!cancelled) setSelectedSession(data);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.response?.data?.detail || "Unable to load this conversation.");
      });
    return () => { cancelled = true; };
  }, [selectedSessionId]);

  // Live updates: subscribe to the selected session's WebSocket group.
  useEffect(() => {
    if (!selectedSessionId || !WS_BASE) return undefined;

    const socket = new WebSocket(`${WS_BASE}/ws/support/chat/${selectedSessionId}/`);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === "message") {
        const incoming = data.message;
        setSelectedSession((current) => {
          if (!current || current.session_id !== selectedSessionId) return current;
          if ((current.messages || []).some((message) => message.id === incoming.id)) return current;
          return { ...current, updated_at: incoming.timestamp, messages: [...(current.messages || []), incoming] };
        });
        setSessions((current) => sortSessions(current.map((session) => (
          session.session_id === selectedSessionId
            ? { ...session, updated_at: incoming.timestamp, latest_message_content: incoming.content, latest_message_timestamp: incoming.timestamp }
            : session
        ))));
      } else if (data.type === "status") {
        setSelectedSession((current) => (
          current && current.session_id === selectedSessionId ? { ...current, status: data.status } : current
        ));
        setSessions((current) => current.map((session) => (
          session.session_id === selectedSessionId ? { ...session, status: data.status } : session
        )));
      }
    };

    return () => {
      socket.close();
      if (wsRef.current === socket) wsRef.current = null;
    };
  }, [selectedSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [selectedSessionId, selectedSession?.messages?.length]);

  async function sendReply(event) {
    event.preventDefault();
    const content = reply.trim();
    if (!content || !selectedSession || sending) return;

    setSending(true);
    setError("");
    try {
      const { data } = await axiosInstance.post("/api/support/chat/support/reply/", {
        session_id: selectedSession.session_id,
        content,
      });
      const newMessage = {
        id: data.id,
        sender_type: "support",
        agent_name: "Procman Support Team",
        content: data.content,
        timestamp: data.timestamp,
      };
      setSessions((current) => sortSessions(current.map((session) => (
        session.session_id === selectedSession.session_id
            ? { ...session, status: data.status, updated_at: data.timestamp, latest_message_content: newMessage.content, latest_message_timestamp: newMessage.timestamp, message_count: session.message_count + 1 }
          : session
      ))));
          setSelectedSession((current) => current ? { ...current, status: data.status, updated_at: data.timestamp, messages: [...(current.messages || []), newMessage] } : current);
      setReply("");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Unable to send the reply.");
    } finally {
      setSending(false);
    }
  }

  const visibleSessions = sessions.filter((session) => {
    const searchable = `${session.display_name} ${session.contact_email || ""} ${getPreview(session)}`.toLowerCase();
    return searchable.includes(query.trim().toLowerCase());
  });

  return (
    <div className="support-inbox">
      <div className="support-inbox__heading">
        <div>
          <h1>Support Inbox</h1>
          <p>All customer conversations with the Procman Assistant and support team.</p>
        </div>
        <button className="support-inbox__refresh" type="button" onClick={() => loadSessions()} disabled={loading} title="Refresh conversations">
          <RefreshCw size={16} className={loading ? "support-inbox__spin" : ""} />
          Refresh
        </button>
      </div>

      {error && <div className="support-inbox__error" role="alert">{error}</div>}

      <section className="support-inbox__workspace">
        <aside className="support-inbox__list">
          <div className="support-inbox__search">
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search conversations" aria-label="Search conversations" />
          </div>
          <div className="support-inbox__count">{visibleSessions.length} active conversation{visibleSessions.length === 1 ? "" : "s"}</div>
          <div className="support-inbox__session-list">
            {loading ? <p className="support-inbox__empty">Loading conversations...</p> : visibleSessions.length === 0 ? <p className="support-inbox__empty">No conversations found.</p> : visibleSessions.map((session) => (
              <button key={session.session_id} type="button" className={`support-inbox__session ${session.session_id === selectedSessionId ? "is-selected" : ""}`} onClick={() => setSelectedSessionId(session.session_id)}>
                <span className="support-inbox__avatar">{(session.display_name || "A").charAt(0).toUpperCase()}</span>
                <span className="support-inbox__session-body">
                  <span className="support-inbox__session-top"><strong>{session.display_name}</strong><time>{formatTime(session.updated_at)}</time></span>
                  <span className="support-inbox__email">{session.contact_email || "Anonymous visitor"}</span>
                  <span className="support-inbox__preview">{getPreview(session)}</span>
                </span>
                <span className={`support-inbox__status status-${session.status}`}>{STATUS_LABELS[session.status] || session.status}</span>
              </button>
            ))}
          </div>
        </aside>

        <main className="support-inbox__conversation">
          {selectedSession ? <>
            <header className="support-inbox__conversation-header">
              <span className="support-inbox__avatar">{(selectedSession.display_name || "A").charAt(0).toUpperCase()}</span>
              <div><h2>{selectedSession.display_name}</h2><p>{selectedSession.contact_email || "Anonymous visitor"}</p></div>
              <span className={`support-inbox__status status-${selectedSession.status}`}>{STATUS_LABELS[selectedSession.status] || selectedSession.status}</span>
            </header>
            <div className="support-inbox__messages">
              {(selectedSession.messages || []).map((message) => (
                <article key={message.id} className={`support-inbox__message support-inbox__message--${message.sender_type}`}>
                  {message.sender_type !== "user" && <span className="support-inbox__sender">{message.sender_type === "support" ? "Procman Support Team" : "Procman Assistant"}</span>}
                  <p>{message.content}</p>
                  <time>{formatTime(message.timestamp)}</time>
                </article>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <form className="support-inbox__composer" onSubmit={sendReply}>
              <textarea value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Reply as Procman Support Team" aria-label="Support reply" rows={2} disabled={sending} />
              <button type="submit" disabled={!reply.trim() || sending} title="Send reply"><Send size={18} /> <span>{sending ? "Sending" : "Send"}</span></button>
            </form>
          </> : <div className="support-inbox__empty support-inbox__empty--conversation"><MessageCircle size={30} /> Select a conversation to view its history.</div>}
        </main>
      </section>
    </div>
  );
}